import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

interface VoteDetail {
  id: string;
  code: string;
  name: string;
  type: string;
  programmes?: Array<{
    id: string;
    code: string;
    name: string;
    subProgrammes?: Array<{ id: string; code: string; name: string }>;
  }>;
}

export function CoaAdmin() {
  const { persona } = useAuth();
  const { votes, fiscalYears, reload: reloadRef } = useRefData();
  const isAdmin = persona?.roles.includes('MOF_COA_ADMIN') ?? false;
  const [error, setError] = useState<string | null>(null);
  const [selectedVote, setSelectedVote] = useState('');
  const [showCombo, setShowCombo] = useState(false);
  const [showProgramme, setShowProgramme] = useState(false);
  const [newYear, setNewYear] = useState('');

  const voteQ = useApiQuery<VoteDetail>(selectedVote ? `/api/v1/coa/votes/${selectedVote}` : null);
  const combosQ = useApiQuery<{ items: Combination[] }>(
    selectedVote ? `/api/v1/coa/combinations?voteId=${selectedVote}&limit=100` : null,
  );

  const transition = async (fyId: string, to: string) => {
    setError(null);
    try {
      await api.post(`/api/v1/coa/fiscal-years/${fyId}/transition`, { to });
      reloadRef();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Transition failed');
    }
  };

  const createYear = async () => {
    setError(null);
    try {
      await api.post('/api/v1/coa/fiscal-years', { year: Number.parseInt(newYear, 10) });
      setNewYear('');
      reloadRef();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Create failed');
    }
  };

  const toggleCombo = async (c: Combination) => {
    setError(null);
    try {
      await api.post(`/api/v1/coa/combinations/${c.id}/${c.active ? 'lock' : 'unlock'}`);
      combosQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    }
  };

  const NEXT: Record<string, string | undefined> = { PLANNING: 'OPEN', OPEN: 'CLOSING', CLOSING: 'CLOSED' };

  return (
    <div>
      <PageHeader
        title="Chart of accounts"
        subtitle="Fiscal years, ministry votes, PPBB programmes and postable budget-line combinations"
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Fiscal years</h2>
        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-3">
            {fiscalYears
              .slice()
              .sort((a, b) => a.year - b.year)
              .map((f) => (
                <div key={f.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <span className="font-semibold">{f.year}</span>
                  <StatusBadge status={f.status} />
                  {isAdmin && NEXT[f.status] ? (
                    <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => transition(f.id, NEXT[f.status]!)}>
                      → {NEXT[f.status]}
                    </button>
                  ) : null}
                </div>
              ))}
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <input
                  className="field-input w-28"
                  type="number"
                  placeholder="Year"
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                />
                <button className="btn-primary !py-1.5 text-xs" onClick={createYear} disabled={!newYear}>
                  + Create
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-end justify-between gap-3">
        <div className="max-w-md flex-1">
          <span className="field-label">Ministry vote</span>
          <select className="field-input" value={selectedVote} onChange={(e) => setSelectedVote(e.target.value)}>
            <option value="">Select a vote to manage…</option>
            {votes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} — {v.name}
              </option>
            ))}
          </select>
        </div>
        {isAdmin && selectedVote ? (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setShowProgramme(true)}>
              + Programme / sub-programme
            </button>
            <button className="btn-primary" onClick={() => setShowCombo(true)}>
              + Budget-line combination
            </button>
          </div>
        ) : null}
      </div>

      {selectedVote ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="card p-4">
            <h3 className="mb-2 text-sm font-bold text-slate-700">PPBB structure</h3>
            {voteQ.loading ? (
              <Spinner />
            ) : (voteQ.data?.programmes?.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No programmes yet.</p>
            ) : (
              voteQ.data!.programmes!.map((p) => (
                <div key={p.id} className="mb-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold">
                    {p.code} — {p.name}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                    {(p.subProgrammes ?? []).map((s) => (
                      <li key={s.id}>
                        {s.code} — {s.name}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
          <div className="card p-4">
            <h3 className="mb-2 text-sm font-bold text-slate-700">Budget-line combinations</h3>
            {combosQ.loading ? (
              <Spinner />
            ) : (combosQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No combinations yet.</p>
            ) : (
              combosQ.data!.items.map((c) => (
                <div key={c.id} className="mb-1.5 flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <span className="text-xs">{comboLabel(c)}</span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={c.active ? 'ACTIVE' : 'SUSPENDED'} />
                    {isAdmin ? (
                      <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => toggleCombo(c)}>
                        {c.active ? 'Lock' : 'Unlock'}
                      </button>
                    ) : null}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Pick a vote to see its programmes and postable combinations.</p>
      )}

      {showProgramme && voteQ.data ? (
        <ProgrammeModal
          vote={voteQ.data}
          onClose={() => setShowProgramme(false)}
          onDone={() => {
            setShowProgramme(false);
            voteQ.reload();
          }}
        />
      ) : null}
      {showCombo && voteQ.data ? (
        <ComboModal
          vote={voteQ.data}
          onClose={() => setShowCombo(false)}
          onDone={() => {
            setShowCombo(false);
            combosQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ProgrammeModal({ vote, onClose, onDone }: { vote: VoteDetail; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<'programme' | 'sub'>('programme');
  const [programmeId, setProgrammeId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'programme') {
        await api.post(`/api/v1/coa/votes/${vote.id}/programmes`, { code, name });
      } else {
        await api.post(`/api/v1/coa/programmes/${programmeId}/subprogrammes`, { code, name });
      }
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add PPBB structure — ${vote.code}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Type">
          <select className="field-input" value={mode} onChange={(e) => setMode(e.target.value as 'programme' | 'sub')}>
            <option value="programme">Programme (Pxx)</option>
            <option value="sub">Sub-programme (SPxx)</option>
          </select>
        </Field>
        {mode === 'sub' ? (
          <Field label="Parent programme">
            <select className="field-input" value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
              <option value="">Select programme…</option>
              {(vote.programmes ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={mode === 'programme' ? 'P03' : 'SP02'} />
          </Field>
          <Field label="Name" className="col-span-2">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !name || (mode === 'sub' && !programmeId)}>
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ComboModal({ vote, onClose, onDone }: { vote: VoteDetail; onClose: () => void; onDone: () => void }) {
  const econQ = useApiQuery<{ items: Array<{ id: string; gfsCode: string; name: string }> }>('/api/v1/coa/econ-items?limit=100');
  const fundQ = useApiQuery<{ items: Array<{ id: string; code: string; name: string }> }>('/api/v1/coa/fund-sources');
  const [programmeId, setProgrammeId] = useState('');
  const [subProgrammeId, setSubProgrammeId] = useState('');
  const [econItemId, setEconItemId] = useState('');
  const [fundSourceId, setFundSourceId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const programme = (vote.programmes ?? []).find((p) => p.id === programmeId);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/coa/combinations', {
        voteId: vote.id,
        programmeId,
        subProgrammeId,
        econItemId,
        fundSourceId,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`New budget-line combination — ${vote.code}`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Programme">
            <select
              className="field-input"
              value={programmeId}
              onChange={(e) => {
                setProgrammeId(e.target.value);
                setSubProgrammeId('');
              }}
            >
              <option value="">Select…</option>
              {(vote.programmes ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sub-programme">
            <select className="field-input" value={subProgrammeId} onChange={(e) => setSubProgrammeId(e.target.value)}>
              <option value="">Select…</option>
              {(programme?.subProgrammes ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Economic item (GFS)">
            <select className="field-input" value={econItemId} onChange={(e) => setEconItemId(e.target.value)}>
              <option value="">Select…</option>
              {(econQ.data?.items ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.gfsCode} — {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fund source">
            <select className="field-input" value={fundSourceId} onChange={(e) => setFundSourceId(e.target.value)}>
              <option value="">Select…</option>
              {(fundQ.data?.items ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} — {x.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !programmeId || !subProgrammeId || !econItemId || !fundSourceId}
          >
            {busy ? 'Creating…' : 'Create combination'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
